'use client'

import React, {lazy, Suspense} from 'react'
import grid, {gridItem} from '~/design-system/foundations/Grid'
import HgTextBlock, {
  type HgTextBlockProps,
} from '~/design-system/hg/components/HgTextBlock'
import {type PartialPartial} from '~/utils/types'
import {HgAspectRatio} from '../../components'
import {hgFeaturedMediaVariants} from './variant'
import type {HgEmbedProps} from '~/design-system/hg/components/HgEmbed'
import {cn} from '~/design-system/utils'

const HgEmbed = lazy(
  async () => await import('~/design-system/hg/components/HgEmbed/index')
)

export type HgFeaturedMediaProps = {
  assetTwoOneRatio: React.ReactNode
  assetFourFiveRatio: React.ReactNode
  embed: HgEmbedProps | null
  alignment: 'left' | 'center'
} & PartialPartial<Pick<HgTextBlockProps, 'buttonProps' | 'title' | 'body'>, 'title'>

export default function HgFeaturedMedia({
  assetTwoOneRatio,
  assetFourFiveRatio,
  embed,
  title,
  body,
  alignment,
  ...textBlockProps
}: HgFeaturedMediaProps) {
  const {textBlock: textBlockVariant} = hgFeaturedMediaVariants({alignment})

  const alignedLeft = alignment === 'left'

  return (
    <div className="w-full">
      <div className={grid({className: 'col-span-full mx-auto pt-s9'})}>
        {embed ? (
          <div
            className={gridItem({
              size: 'max',
              class: cn(alignedLeft && 'col-start-1 lg:col-start-3'),
            })}
          >
            <Suspense fallback={null}>
              <HgEmbed {...embed} className={cn(alignedLeft && 'justify-start')} />
            </Suspense>
          </div>
        ) : (
          <>
            <HgAspectRatio
              aspectRatio="2:1"
              topSpacing="s9"
              classNames={{
                wrapper: gridItem({
                  size: 'max',
                  class: 'hidden sm:inline-flex',
                }),
              }}
            >
              {assetTwoOneRatio}
            </HgAspectRatio>
            <HgAspectRatio
              aspectRatio="4:5"
              topSpacing="s9"
              classNames={{
                wrapper: gridItem({
                  size: 'max',
                  class: 'sm:hidden',
                }),
              }}
            >
              {assetFourFiveRatio}
            </HgAspectRatio>
          </>
        )}
        <div className={textBlockVariant()}>
          <HgTextBlock
            title={title}
            body={body}
            {...textBlockProps}
            alignment={alignment}
          />
        </div>
      </div>
    </div>
  )
}
